import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  OnInit,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { ISnapZoomOptions, IViewportOptions, Viewport } from 'pixi-viewport';
import { Application, Renderer, FederatedMouseEvent, Sprite, Assets, Point, Graphics } from 'pixi.js';
import { filter, ReplaySubject, Subject, take } from 'rxjs';
import { initDevtools } from '@pixi/devtools';

import { delayFor } from 'app/core/utilities/async-utils';
import { CTAService } from 'app/modules/social/services/cta/cta.service';
// import { Disco } from 'app/modules/social/social-pixi-canvas/disco';
import { LocationHelperService } from 'app/modules/social/social-pixi-canvas/location-helper.service';
import { ExtendedISnapZoomOptions, ZoomDirection } from 'app/modules/social/social-pixi-canvas/scene-manager';
import { Loggable } from 'app/utils/logging/loggable';
import CTAItem from 'entity/CTAItem';
import SocialTheme from 'entity/SocialTheme';

import { RoomSize, RoomSizes } from '../create-room/room-size-card/constants';

import { EditableItem } from './editable-item';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { LaunchDarklyFlags, PixiInspector } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Store } from '@ngrx/store';

const ZOOM_OFFSET = 0.1;

@Component({
  selector: 'app-pixi-item-editor',
  imports: [MatIcon, CommonModule],
  templateUrl: './pixi-item-editor.component.html',
  styleUrl: './pixi-item-editor.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PixiItemEditorComponent extends Loggable implements OnChanges, OnInit, OnDestroy {
  @Input()
  public theme!: SocialTheme;

  @Input()
  protected set listItemSelectedId(value: string | undefined) {
    if (value) {
      void this.listItemSelection(value);
    }
  }

  private _ctaItems = signal<CTAItem[]>([]);
  // private panning = false;
  public get ctaItems(): CTAItem[] {
    return this._ctaItems();
  }
  @Input()
  public set ctaItems(value: CTAItem[]) {
    if (value && value.length) {
      // get the removed items
      const removedItem = this.getRemovedItems(value, this.ctaItems);
      if (removedItem) {
        removedItem.forEach((item) => this.removeCTAItem(item));
      }

      // get the added items
      const addedItems = this.getAddedItems(value, this.ctaItems);
      if (addedItems) {
        addedItems.forEach((item) => this.addCTAItemContainer(item));
      }
      this._ctaItems.set(value);

      // TODO: update items?
    }
  }

  @Output()
  public itemSelected = new EventEmitter<string>();
  private destroyed$ = new ReplaySubject<boolean>(1);

  public snapZoomOptions: ExtendedISnapZoomOptions = {
    removeOnComplete: true,
    removeOnInterrupt: false,
    time: 1000,
    ease: 'easeInOutSine',
  };
  private zoomIntervalId: NodeJS.Timeout | null = null;
  private isZooming = false;
  public inDisabled = false;
  public outDisabled = false;
  public zoomDirection: ZoomDirection = { value: 'none' };
  public zoomDirection$ = new Subject<ZoomDirection>();
  private zoomTimeout: number | undefined;
  // when the zoom is reset, the minZoom is set to the current width of the app
  private minZoom = 0.3;
  private focusZoom = 1;
  private maxZoom = 1;

  public get zoom(): number {
    if (!this.viewport) {
      return 1;
    }
    return this.viewport.scale.x;
  }
  public set zoom(value: number | { zoom: number; center: Point }) {
    if (!value) {
      return;
    }
    if (typeof value === 'number') {
      this.viewport.snapZoom({
        time: 1000,
        width: value,
        // height: value,
        ease: 'linear',
      });
    } else {
      const zoom = Math.floor(value.zoom * 100) / 100;
      this.viewport.snapZoom({
        time: 1000,
        width: this.viewport.screenWidth / zoom,
        // height: value.zoom * this.viewport.screenHeight,
        center: value.center,
        ease: 'linear',
      });
    }
  }

  private get center() {
    return this.viewport.center;
  }

  private set center({ x, y }: { x: number; y: number }) {
    this.viewport.moveCenter(x, y);
  }

  protected app!: Application;
  public element?: HTMLElement;
  private dragTargetContainer: EditableItem | null = null;
  private selected: EditableItem | null = null;
  private dragged = 0;
  private minimumDragDistance = 10;
  private roomSize?: RoomSize;
  private viewport!: Viewport;

  // region lifecycle
  public constructor(
    private elementRef: ElementRef,
    private ctaService: CTAService,
    private _toasterService: ToastrService,
    private locationHelperService: LocationHelperService,
    private store: Store,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.roomSize = RoomSizes.find((roomSize) => roomSize.size === this.theme?.imageWidth) ?? RoomSizes[0];

    this.element = this.elementRef.nativeElement.querySelector('#pixiItemEditor');

    this.app = new Application({
      background: this.theme?.colorBackground,
      resizeTo: this.element,
      autoDensity: true,
      resolution: window.devicePixelRatio,
      autoStart: true,
      powerPreference: 'high-performance',
      eventMode: 'passive',
      eventFeatures: {
        move: true,
        globalMove: false,
        click: true,
        wheel: true,
      },
    });

    if (this.app && this.element) {
      this.element.appendChild(this.app.view as unknown as Node);
    }

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pixi_inspector)
      .pipe(
        take(1),
        filter((pixi_inspector) => pixi_inspector === PixiInspector.ITEM),
      )
      .subscribe(() => {
        void initDevtools({
          app: this.app,
        });
      });

    const viewportOptions: IViewportOptions = {
      worldWidth: 4000, //this.roomSize!.size,
      worldHeight: 4000, //this.roomSize!.size,
      // events: this.app.renderer.events,
    };
    this.viewport = new Viewport(viewportOptions)
      .clamp({
        left: false,
        right: false,
        top: false,
        bottom: false,
        direction: 'all',
        underflow: 'center',
      })
      .drag()
      // .wheel()
      // .decelerate()
      // .bounce()
      .clampZoom({
        minScale: 0.3,
        maxScale: 1,
      })
      .fitWorld();

    this.elementRef.nativeElement.appendChild(this.app.view);
    this.viewport.name = 'viewport';
    this.app.stage.addChild(this.viewport);

    await this.addBackground();

    // this.enableDragPanning();

    // deselect on click
    this.viewport.on('pointerdown', this.handlePointerDown.bind(this));
    // this.minZoom = this.app.renderer.width / this.roomSize.size;
    this.resetZoom();
  }

  // TODO: remove this
  // @HostListener('window:keydown', ['$event'])
  // private handleKeyDown(event: KeyboardEvent) {
  //   if (event.key === 'Escape') {
  //     new Disco().init(this.app);
  //   }
  // }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    // if we end up using any global event listeners, we should remove them here
    this.app.destroy();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // TODO: move this logic into setter
    if (changes && changes.listItemSelectedId && changes.listItemSelectedId.currentValue) {
      void this.listItemSelection(changes.listItemSelectedId.currentValue);
    }
  }

  // endregion lifecycle

  // region item drag logic
  private deselectAllItems(e: Event) {
    if (this.selected) {
      this.blurItem();
      this.selected.deselect();
      this.selected.cancelMove(e);
    }
    this.selected = null;
    this.selectItem();
  }

  private handlePointerDown(e: Event) {
    if (this.selected) {
      this.blurItem();
      this.selected.deselect();
      this.selected.cancelMove(e);
    }
    this.selected = null;
    this.selectItem();
  }

  private onDragContainerStart(containerItem: EditableItem) {
    if (this.selected && this.selected?.name !== containerItem.name) {
      this.selected?.deselect();
    }
    this.selected = containerItem;

    this.dragged = 0;
    this.dragTargetContainer = containerItem;
    this.dragTargetContainer.lastX = containerItem.x;
    this.dragTargetContainer.lastY = containerItem.y;
    this.dragTargetContainer.alpha = 0.8;
    this.dragTargetContainer.cursor = 'move';
    this.selectItem(containerItem.name!);
  }

  private onDragContainerMove(event: FederatedMouseEvent) {
    this.dragged += 1;
    if (this.dragTargetContainer) {
      // @ts-expect-error leave stage argument null to prevent location errors after pan
      this.dragTargetContainer.parent.toLocal(event.global, null, this.dragTargetContainer.position);
    }
  }

  private onDragContainerEnd() {
    if (this.dragTargetContainer) {
      // if the drag distance is less than the minimum, reset the position
      if (this.dragged < this.minimumDragDistance) {
        this.dragTargetContainer.x = this.dragTargetContainer.lastX;
        this.dragTargetContainer.y = this.dragTargetContainer.lastY;
      } else {
        this.dragTargetContainer.lastX = Math.floor(this.dragTargetContainer.x);
        this.dragTargetContainer.lastY = Math.floor(this.dragTargetContainer.y);
        this.dragTargetContainer.buttonVisibility(true);
        this.focusOnItem(this.dragTargetContainer);
      }
      this.dragTargetContainer.cursor = 'pointer';
      this.dragTargetContainer.alpha = 1;
      this.dragTargetContainer = null;
    }
  }
  // endregion drag logic

  // region zoom logic
  public async zoomIn(): Promise<void> {
    if (this.zoom < this.maxZoom) {
      await this.zoomTo(this.zoom + ZOOM_OFFSET, {
        ...this.snapZoomOptions,
        time: 500,
        removeOnInterrupt: true,
        ease: 'linear',
      });
    }
  }

  public async zoomOut(): Promise<void> {
    await this.zoomTo(this.zoom - ZOOM_OFFSET, {
      ...this.snapZoomOptions,
      time: 500,
      removeOnInterrupt: true,
      ease: 'linear',
    });
  }

  public async zoomTo(zoomIn: number, options?: ISnapZoomOptions): Promise<void> {
    // round to 2 decimal places
    const zoom = Math.floor(zoomIn * 100) / 100;
    if (options) {
      // recalculate width from percentage
      options.width = this.viewport.screenWidth / zoom;
      this.viewport.snapZoom(options);

      // update state in ZoomPixiWorldControlsComponent
      this.zoomDirection$.next({ value: zoom > this.zoom ? 'in' : 'out' });
      window.clearTimeout(this.zoomTimeout);
      this.zoomTimeout = window.setTimeout(async () => {
        this.zoomDirection$.next({ value: 'none' });
        await this.zoomCleanup();
      }, this.snapZoomOptions.time);
    } else {
      this.viewport.setZoom(zoom, true);
      await this.zoomCleanup();
    }
  }

  public startZoom(zoomIn: boolean): void {
    // Immediately trigger the zoom action
    void this.handleZoom(zoomIn);

    // Set up an interval for continuous zooming
    this.zoomIntervalId = setInterval(() => {
      void this.handleZoom(zoomIn);
    }, 500);
  }

  public stopZoom(): void {
    if (this.zoomIntervalId) {
      clearInterval(this.zoomIntervalId);
      this.zoomIntervalId = null;
    }
  }
  public async handleZoom(zoomIn: boolean): Promise<void> {
    // Prevent overlapping zoom actions
    if (this.isZooming) {
      return;
    }
    try {
      this.isZooming = true;
      if (zoomIn) {
        await this.zoomIn();
      } else {
        await this.zoomOut();
      }
      // this.checkBounds();
    } finally {
      // Allow the next zoom action
      this.isZooming = false;
    }
  }

  private async zoomCleanup(): Promise<void> {
    await delayFor(1000);
    // TODO: are we adding a custom cursor?
    // this.customCursor.scale.set(1 / this.zoom);
  }

  public resetZoom(): void {
    this.zoom = {
      zoom: this.minZoom,
      center: new Point(2000, 1000),
    };
  }

  // endregion zoom logic

  // region utilities

  // private legalMove({ x, y }: Point): Point {
  //   if (this.roomSize) {
  //     const halfWidth = this.app.canvas.width / 2 / this._zoom;
  //     const halfHeight = this.app.canvas.height / 2 / this._zoom;

  //     // Constrain x within room boundaries
  //     const legalX = Math.max(halfWidth, Math.min(this.roomSize.size - halfWidth, x));
  //     // Constrain y within room boundaries
  //     const legalY = Math.max(halfHeight, Math.min(this.roomSize.size - halfHeight, y));
  //     return new Point(legalX, legalY);
  //   } else {
  //     return new Point(x, y);
  //   }
  // }

  private enableDragPanning() {
    this.viewport
      .on('pointerdown', () => {
        (this.app.view as HTMLCanvasElement).style.cursor = 'move';
        // this.panning = true;
      })
      .on('pointerup', () => {
        (this.app.view as HTMLCanvasElement).style.cursor = 'auto';
        // this.panning = false;
      })
      .on('pointerupoutside', () => {
        (this.app.view as HTMLCanvasElement).style.cursor = 'auto';
        // this.panning = false;
      });
  }

  private focusOnItem(item: EditableItem) {
    // const { x, y } = item;
    // const { height, width } = item.getBounds();
    // const { height: appHeight, width: appWidth } = this.app.renderer.screen;
    // const { x: stageX, y: stageY } = this.app.stage;
    // const offsetX = (appWidth / 2 - x - width / 2) * this.focusZoom;
    // const offsetY = (appHeight / 2 - y - height / 2) * this.focusZoom;
    // const globalPosition = this.viewport.toWorld(x + width / 2, y + width / 2);
    if (item) {
      if (this.zoom === this.focusZoom) {
        // TODO: slow me down
        this.center = { x: item.x, y: item.y };
      } else {
        this.zoom = {
          zoom: this.focusZoom,
          center: new Point(item.x || 0, item.y || 0),
        };
      }
    }
  }

  public blurItem() {
    this.resetZoom();
  }

  private setDraggable(obj: EditableItem): void {
    obj.eventMode = 'static';

    obj
      .on('pointerdown', () => this.onDragContainerStart(obj))
      .on('pointerup', () => this.onDragContainerEnd())
      .on('pointerupoutside', () => this.onDragContainerEnd())
      .on('pointermove', (event) => this.onDragContainerMove(event))
      .on('click', (event) => {
        this.focusOnItem(this.selected!);
      });
  }

  public async addCTAItemContainer(item: CTAItem): Promise<void> {
    if (item.imageURI) {
      const container = await new EditableItem(
        this.app.renderer as Renderer,
        item,
        this.ctaService,
        this._toasterService,
        this.locationHelperService,
      ).init();

      this.viewport.addChild(container);
      this.setDraggable(container);
      if (!container.imageSprite) {
        return;
      }
      container.imageSprite.cursor = 'pointer';
      container.imageSprite.eventMode = 'static';
      container.imageSprite.on('pointerdown', (event) => {
        event.stopPropagation();
        container.select();

        this.onDragContainerStart(container);
      });
    }
  }

  public removeCTAItem(item: CTAItem): void {
    if (item.imageURI && this.checkItemExist(item)) {
      const spriteChild = this.viewport.getChildByName(item.CTAItemId?.toString() ?? '');
      if (spriteChild) {
        this.app.stage.removeChild(spriteChild);
      }
    }
  }

  private async loadBackgroundSprite(imageURI: string, i: number) {
    const sprite = new Sprite(await Assets.load(imageURI));
    sprite.name = 'background';
    sprite.width = this.roomSize?.size ?? 0;
    sprite.height = this.roomSize?.size ?? 0;
    // sprite.scale.set(1.5);
    this.viewport.addChildAt(sprite, i);
  }

  private async addBackground() {
    const { imageFarURI, imageMidURI, imageNearURI, tiles = [] } = this.theme || {};
    if (tiles.length) {
      const adjustment = 4;
      for (const tile of tiles) {
        const image = tile.uri ?? '';
        const sprite = new Sprite(await Assets.load(image));
        sprite.x = (tile.x ?? 0) / adjustment + 1000;
        sprite.y = (tile.y ?? 0) / adjustment + 1000;
        sprite.width = (tile.width ?? 0) / adjustment;
        sprite.height = (tile.height ?? 0) / adjustment;
        this.viewport.addChildAt(sprite, 0);
      }
    } else if (imageFarURI && imageMidURI && imageNearURI) {
      await this.loadBackgroundSprite(imageFarURI, 0);
      await this.loadBackgroundSprite(imageMidURI, 1);
      await this.loadBackgroundSprite(imageNearURI, 2);
    }
    const border = new Graphics()
      .drawRect(0, 0, this.roomSize?.size ?? 0, this.roomSize?.size ?? 0)
      .beginFill(0xffffff, 0)
      .lineStyle(50, 0x000000, 1);
    this.viewport.addChild(border);
  }

  private checkItemExist(item: CTAItem): boolean {
    return this.viewport.getChildByName(item.CTAItemId?.toString() ?? '') !== undefined;
  }

  private getRemovedItems(currentValue: CTAItem[], previousValue: CTAItem[]): CTAItem[] {
    const removedItem = previousValue.filter((obj2) => {
      if (currentValue) {
        return !currentValue.some((obj1) => obj2.CTAItemId === obj1.CTAItemId);
      }
    });

    return removedItem;
  }

  private getAddedItems(currentValue: CTAItem[], previousValue: CTAItem[]): CTAItem[] {
    const addedItem = currentValue.filter((obj2) => {
      if (previousValue) {
        return !previousValue.some((obj1) => obj2.CTAItemId === obj1.CTAItemId);
      }
    });

    return addedItem;
  }

  private selectItem(itemId?: string): void {
    this.itemSelected.emit(itemId);
  }

  private async listItemSelection(itemId: string): Promise<void> {
    const containerItem = this.viewport.getChildByName(itemId) as EditableItem;

    if (containerItem) {
      if (this.selected && this.selected?.name !== containerItem.name) {
        this.blurItem();
        this.selected?.deselect();
        // delay to zoom out and clear the stage x and y
        await delayFor(1100);
      }

      this.selected = containerItem;
      this.selected.select();
      this.focusOnItem(this.selected);
    }
  }

  private deleteAllCTAItems(): void {
    this.ctaItems.forEach((item) => this.removeCTAItem(item));
  }

  // endregion utilities
}
